<template>
  <teleport to="body">
    <transition name="vp">
      <div
        v-if="open"
        class="vp"
        @click.self="close"
        @mousemove="poke"
      >
        <button class="vp__close" type="button" :aria-label="$t('a11y.close')" @click="close">&times;</button>

        <figure class="vp__stage" ref="stage">
          <!-- YouTube link → embed via iframe (our custom controls below can't
               drive a cross-origin player, so they're skipped for this case) -->
          <iframe
            v-if="ytEmbed"
            class="vp__iframe"
            :src="ytEmbed"
            title="YouTube video"
            frameborder="0"
            allow="accelerator; autoplay; encrypted-media; picture-in-picture"
            allowfullscreen
          ></iframe>

          <template v-else>
          <video
            ref="video"
            class="vp__video"
            :src="src"
            :poster="poster || undefined"
            playsinline
            preload="metadata"
            @click="togglePlay"
            @timeupdate="onTime"
            @loadedmetadata="onLoaded"
            @play="playing = true"
            @pause="playing = false"
            @ended="onEnded"
            @waiting="buffering = true"
            @playing="buffering = false"
          ></video>

          <!-- spinner while buffering -->
          <span v-show="buffering && playing" class="vp__spin" aria-hidden="true"></span>

          <!-- big centre play button while paused -->
          <button
            v-show="!playing"
            class="vp__big"
            type="button"
            :aria-label="$t('home.mediaPlay')"
            @click="togglePlay"
          >
            <svg viewBox="0 0 24 24" fill="currentColor" width="36" height="36"><path d="M8 5v14l11-7L8 5z" /></svg>
          </button>

          <!-- custom control bar -->
          <div class="vp__bar" :class="{ 'vp__bar--off': playing && !ctrl }">
            <button class="vp__btn" type="button" :aria-label="playing ? 'Pause' : 'Play'" @click="togglePlay">
              <svg v-if="playing" viewBox="0 0 24 24" fill="currentColor" width="20" height="20"><path d="M6 5h4v14H6zM14 5h4v14h-4z" /></svg>
              <svg v-else viewBox="0 0 24 24" fill="currentColor" width="20" height="20"><path d="M8 5v14l11-7L8 5z" /></svg>
            </button>

            <span class="vp__t">{{ fmt(current) }}</span>

            <input
              class="vp__seek"
              type="range"
              min="0"
              :max="duration || 0"
              step="0.1"
              :value="current"
              :style="seekStyle"
              aria-label="Seek"
              @input="seek($event.target.value)"
            />

            <span class="vp__t vp__t--dur">{{ fmt(duration) }}</span>

            <div class="vp__vol-group">
              <button class="vp__btn" type="button" :aria-label="muted ? 'Unmute' : 'Mute'" @click="toggleMute">
                <svg v-if="muted" viewBox="0 0 24 24" fill="currentColor" width="20" height="20"><path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3a4.5 4.5 0 0 0-2-3.7v7.4a4.5 4.5 0 0 0 2-3.7z" opacity=".35" /><path d="M19 12a7 7 0 0 0-3-5.7M2 2l20 20" stroke="currentColor" stroke-width="2" fill="none" /></svg>
                <svg v-else viewBox="0 0 24 24" fill="currentColor" width="20" height="20"><path d="M3 9v6h4l5 5V4L7 9H3z" /><path d="M16 8.5a4.5 4.5 0 0 1 0 7M18.5 6a8 8 0 0 1 0 12" stroke="currentColor" stroke-width="2" fill="none" /></svg>
              </button>
              <input
                class="vp__vol"
                type="range"
                min="0"
                max="1"
                step="0.05"
                :value="muted ? 0 : volume"
                :style="volStyle"
                aria-label="Volume"
                @input="setVolume($event.target.value)"
              />
            </div>

            <button class="vp__btn" type="button" aria-label="Fullscreen" @click="toggleFs">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="20" height="20"><path d="M4 9V4h5M20 9V4h-5M4 15v5h5M20 15v5h-5" /></svg>
            </button>
          </div>
          </template>
        </figure>

        <p v-if="title" class="vp__title">{{ title }}</p>
      </div>
    </transition>
  </teleport>
</template>

<script>
import { youtubeEmbed } from '@/store/api'

// Appealing, app-styled video player shown in a blurred overlay (like the photo
// lightbox) — deliberately not the default browser/YouTube chrome. When the
// source is a YouTube link it falls back to a plain embedded iframe.
export default {
  name: 'VideoModal',
  props: {
    open: { type: Boolean, default: false },
    src: { type: String, default: '' },
    poster: { type: String, default: '' },
    title: { type: String, default: '' }
  },
  emits: ['close'],
  data() {
    return {
      playing: false,
      muted: false,
      buffering: false,
      current: 0,
      duration: 0,
      volume: 1,
      ctrl: true // controls visible (auto-hide while playing)
    }
  },
  computed: {
    // A YouTube src → its embed URL (autoplay on open); '' for uploaded clips.
    ytEmbed() {
      const base = youtubeEmbed(this.src)
      return base ? `${base}?autoplay=1&rel=0` : ''
    },
    seekStyle() {
      const pct = this.duration ? (this.current / this.duration) * 100 : 0
      return { background: `linear-gradient(to right, #1D5DFF ${pct}%, rgba(255,255,255,.22) ${pct}%)` }
    },
    volStyle() {
      const pct = (this.muted ? 0 : this.volume) * 100
      return { background: `linear-gradient(to right, #fff ${pct}%, rgba(255,255,255,.22) ${pct}%)` }
    }
  },
  watch: {
    open(v) {
      document.body.style.overflow = v ? 'hidden' : ''
      if (v) {
        this.current = 0
        this.ctrl = true
        this.$nextTick(() => {
          const el = this.$refs.video
          if (!el) return
          this.muted = el.muted
          this.volume = el.volume
          // Try to start playing; if the browser blocks autoplay, the big play
          // button stays and one tap starts it.
          const p = el.play()
          if (p && p.catch) p.catch(() => { this.playing = false })
        })
      } else {
        this.stop()
      }
    }
  },
  mounted() {
    window.addEventListener('keydown', this.onKey)
  },
  beforeUnmount() {
    window.removeEventListener('keydown', this.onKey)
    document.body.style.overflow = ''
    if (this._hide) clearTimeout(this._hide)
  },
  methods: {
    fmt(s) {
      s = Math.max(0, Math.floor(s || 0))
      const m = Math.floor(s / 60)
      const sec = String(s % 60).padStart(2, '0')
      return `${m}:${sec}`
    },
    togglePlay() {
      const el = this.$refs.video
      if (!el) return
      if (el.paused) el.play()
      else el.pause()
      this.poke()
    },
    onLoaded() {
      const el = this.$refs.video
      this.duration = el ? el.duration || 0 : 0
    },
    onTime() {
      const el = this.$refs.video
      if (el) this.current = el.currentTime
    },
    onEnded() {
      this.playing = false
      this.ctrl = true
    },
    seek(v) {
      const el = this.$refs.video
      if (!el) return
      el.currentTime = Number(v)
      this.current = Number(v)
      this.poke()
    },
    toggleMute() {
      const el = this.$refs.video
      if (!el) return
      el.muted = !el.muted
      this.muted = el.muted
      this.poke()
    },
    setVolume(v) {
      const el = this.$refs.video
      if (!el) return
      const vol = Math.min(1, Math.max(0, Number(v)))
      el.volume = vol
      el.muted = vol === 0          // dragging to 0 = mute; above 0 = unmute
      this.volume = vol
      this.muted = el.muted
      this.poke()
    },
    toggleFs() {
      const stage = this.$refs.stage
      if (!stage) return
      if (document.fullscreenElement) document.exitFullscreen()
      else if (stage.requestFullscreen) stage.requestFullscreen()
      this.poke()
    },
    // Reveal controls; auto-hide again a few seconds later while playing.
    poke() {
      this.ctrl = true
      if (this._hide) clearTimeout(this._hide)
      this._hide = setTimeout(() => {
        if (this.playing) this.ctrl = false
      }, 2600)
    },
    stop() {
      const el = this.$refs.video
      if (el) { el.pause(); el.currentTime = 0 }
      this.playing = false
    },
    close() {
      this.stop()
      this.$emit('close')
    },
    onKey(e) {
      if (!this.open) return
      switch (e.key) {
        case 'Escape': this.close(); break
        case ' ': case 'k': e.preventDefault(); this.togglePlay(); break
        case 'ArrowRight': this.seek(Math.min(this.duration, this.current + 5)); break
        case 'ArrowLeft': this.seek(Math.max(0, this.current - 5)); break
        case 'm': this.toggleMute(); break
        case 'f': this.toggleFs(); break
        default: break
      }
    }
  }
}
</script>

<style lang="scss">
.vp {
  position: fixed;
  inset: 0;
  z-index: 4200;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;   // centre the video (+ title) both axes, any screen
  gap: 18px;
  padding: 40px;
  // translucent + blurred so the page shows softly through the sides
  background: rgba(8, 15, 35, 0.62);
  backdrop-filter: blur(16px);
  -webkit-backdrop-filter: blur(16px);
}

.vp__stage {
  position: relative;
  margin: 0;
  display: inline-block;
  line-height: 0;
  // Size to the video like a photo in the lightbox: up to 90vw wide, height
  // follows the clip's aspect ratio (capped so it always fits the screen).
  max-width: 90vw;
  max-height: 86vh;
  border-radius: 18px;
  overflow: hidden;
  background: #05080f;
  box-shadow: 0 30px 80px rgba(0, 0, 0, 0.6), 0 0 0 1px rgba(255, 255, 255, 0.06);
}

.vp__video {
  display: block;
  width: auto;
  height: auto;
  max-width: 90vw;
  max-height: 86vh;
  background: #05080f;
  cursor: pointer;
}

// YouTube embed: fixed 16:9 stage that fits the viewport like the video does
.vp__iframe {
  display: block;
  width: min(90vw, 1280px);
  aspect-ratio: 16 / 9;
  max-height: 86vh;
  border: 0;
  background: #05080f;
}

// Big centre play button while paused
.vp__big {
  position: absolute;
  inset: 0;
  margin: auto;
  width: 84px;
  height: 84px;
  border: 0;
  border-radius: 50%;
  color: #fff;
  background: rgba(19, 102, 196, 0.92);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 10px 34px rgba(13, 79, 158, 0.55);
  transition: transform 0.18s ease, background 0.18s ease;

  svg { margin-left: 4px; }
  &:hover { transform: scale(1.08); background: #1D5DFF; }
  &:active { transform: scale(0.96); }
}

// Buffering spinner
.vp__spin {
  position: absolute;
  inset: 0;
  margin: auto;
  width: 54px;
  height: 54px;
  border-radius: 50%;
  border: 4px solid rgba(255, 255, 255, 0.25);
  border-top-color: #fff;
  animation: vp-spin 0.8s linear infinite;
}
@keyframes vp-spin { to { transform: rotate(360deg); } }

// ── Control bar ──────────────────────────────────────────────────────────────
.vp__bar {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 30px 18px 14px;
  background: linear-gradient(to top, rgba(0, 0, 0, 0.7), transparent);
  opacity: 1;
  transform: translateY(0);
  transition: opacity 0.25s ease, transform 0.25s ease;

  &--off { opacity: 0; transform: translateY(8px); pointer-events: none; }
}

.vp__btn {
  flex: none;
  width: 38px;
  height: 38px;
  border: 0;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.12);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.15s ease, transform 0.15s ease;

  &:hover { background: rgba(255, 255, 255, 0.26); }
  &:active { transform: scale(0.92); }
}

.vp__t {
  flex: none;
  min-width: 42px;
  color: #fff;
  font-size: 13px;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  text-align: center;
  &--dur { opacity: 0.8; }
}

// Custom range: thin rounded track with a blue fill (via inline gradient) + dot
.vp__seek {
  flex: 1;
  height: 6px;
  appearance: none;
  -webkit-appearance: none;
  border-radius: 999px;
  outline: none;
  cursor: pointer;

  &::-webkit-slider-thumb {
    -webkit-appearance: none;
    width: 15px;
    height: 15px;
    border-radius: 50%;
    background: #fff;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.5);
    cursor: pointer;
  }
  &::-moz-range-thumb {
    width: 15px;
    height: 15px;
    border: 0;
    border-radius: 50%;
    background: #fff;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.5);
    cursor: pointer;
  }
}

// Volume: mute button + a compact slider (white fill to distinguish from seek)
.vp__vol-group {
  flex: none;
  display: flex;
  align-items: center;
  gap: 6px;
}
.vp__vol {
  flex: none;
  width: 74px;
  height: 5px;
  appearance: none;
  -webkit-appearance: none;
  border-radius: 999px;
  outline: none;
  cursor: pointer;

  &::-webkit-slider-thumb {
    -webkit-appearance: none;
    width: 13px;
    height: 13px;
    border-radius: 50%;
    background: #fff;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.5);
    cursor: pointer;
  }
  &::-moz-range-thumb {
    width: 13px;
    height: 13px;
    border: 0;
    border-radius: 50%;
    background: #fff;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.5);
    cursor: pointer;
  }
}

.vp__close {
  position: absolute;
  top: 22px;
  right: 26px;
  width: 46px;
  height: 46px;
  border: 0;
  border-radius: 999px;
  font-size: 30px;
  line-height: 1;
  color: #fff;
  background: rgba(255, 255, 255, 0.12);
  cursor: pointer;
  transition: background 0.15s ease, transform 0.15s ease;
  &:hover { background: rgba(255, 255, 255, 0.28); }
  &:active { transform: scale(0.94); }
}

.vp__title {
  max-width: 92vw;
  margin: 0;
  color: #fff;
  font-size: 18px;
  font-weight: 600;
  text-align: center;
  text-shadow: 0 2px 12px rgba(0, 0, 0, 0.5);
}

.vp-enter-active, .vp-leave-active { transition: opacity 0.25s ease; }
.vp-enter-from, .vp-leave-to { opacity: 0; }
.vp-enter-active .vp__stage { transition: transform 0.3s cubic-bezier(0.22, 0.61, 0.36, 1); }
.vp-enter-from .vp__stage { transform: scale(0.94); }

@media (prefers-reduced-motion: reduce) {
  .vp-enter-active, .vp-leave-active { transition: none; }
  .vp-enter-from .vp__stage { transform: none; }
  .vp__spin { animation: none; }
}

@media (max-width: 600px) {
  .vp { padding: 16px; }
  .vp__close { top: 12px; right: 14px; width: 40px; height: 40px; font-size: 26px; }
  .vp__big { width: 64px; height: 64px; }
  .vp__t--dur { display: none; }
  .vp__vol { display: none; }   // hardware volume on mobile; keep the mute button
}
</style>
